import React, { FC, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import REMOVE_STUDENT_SETTINGS from 'graph/settings/common/remove-student-settings-mutation';
import GET_HWT_STUDENT_SETTINGS from 'graph/settings/hwt/get-hwt-student-settings-query';
import UPDATE_HWT_STUDENT_LANGUAGE_SETTING from 'graph/settings/hwt/update-hwt-student-language-setting-mutation';
import { updateCache } from 'graph/update-cache';
import Typography from '@material-ui/core/Typography';
import CommonLocaleSetting from '../common/LocaleSetting';
import { LocaleStudentSettingProps } from '../../../../types';

const getStudentSettingsMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_HWT_STUDENT_SETTINGS,
      variables: { where: { studentId } },
      reducer: (cachedData, { updateHwtStudentSettingLanguage }): { hwtStudentSettings: any } => {
        const [{ value: locale }] = updateHwtStudentSettingLanguage;
        return { hwtStudentSettings: { ...cachedData?.hwtStudentSettings, locale } };
      }
    }
  ])
});

const removeStudentSettingMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_HWT_STUDENT_SETTINGS,
      variables: {
        where: {
          studentId
        }
      },
      reducer: (cachedData): { hwtStudentSettings: any } => {
        return {
          hwtStudentSettings: {
            ...cachedData?.hwtStudentSettings,
            locale: ''
          }
        };
      }
    }
  ])
});

const LocaleSetting: FC<LocaleStudentSettingProps> = props => {
  const { t } = useTranslation('HwtLocaleSetting');
  const studentId = +props.student.id;

  const [updateLocaleSetting] = useMutation(
    UPDATE_HWT_STUDENT_LANGUAGE_SETTING,
    getStudentSettingsMutationCacheUpdate(studentId)
  );

  // Account for removing multiple setting records - the 'HWT' Locale setting currently affects both HWTAPP-22 and HWTAPP-25.
  const [removeLocaleSettings] = useMutation(
    REMOVE_STUDENT_SETTINGS,
    removeStudentSettingMutationCacheUpdate(studentId)
  );

  const removeHwtLocale = useCallback(
    () =>
      removeLocaleSettings({
        variables: {
          where: { studentId, appName: ['hwt-student-22', 'hwt-student-25'], setting: 'locale' }
        }
      }),
    [studentId, removeLocaleSettings]
  );

  const updateHwtLocale = useCallback(
    (value: string) => updateLocaleSetting({ variables: { data: { studentId, value } } }),
    [studentId, updateLocaleSetting]
  );

  const appLocaleSettingsProps = useMemo(
    () => ({
      description: t(
        'With this setting, student navigation and instruction will be displayed in the language chosen. By default, the language option is English.'
      ),
      formLabel: (
        <Typography variant="body2">
          <strong>{t('Note')}: </strong>
          {t(
            'To send assignments to students in their chosen language, the teacher language in the Interactive Digital Teaching Tool (IDTT) must match the student setting.'
          )}
        </Typography>
      ),
      onRemove: removeHwtLocale,
      onUpdate: updateHwtLocale
    }),
    [t, removeHwtLocale, updateHwtLocale]
  );

  return <CommonLocaleSetting {...props} {...appLocaleSettingsProps} />;
};

export default LocaleSetting;
